import { useCallback, useRef } from "react";

import _ from "underscore";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import StrUtils from "../../../utils/StrUtils";
import { View } from "../../../types/PageInterfaces";
import { CardTypes } from "../../TableLayouts/CardsLayout/CardsLayoutEditor";

export type CreateLayoutType =
  | "CardsLayout"
  | "TimelineLayout"
  | "BoardLayout"
  | "TableLayout";

export type CreateTableItem = {
  onSuccess: () => void;
} & (
  | { key: "CardsLayout"; payload: CardsLayoutI }
  | {
      key: "TimelineLayout";
      payload: TimelineLayoutI;
    }
  | { key: "BoardLayout"; payload: BoardLayoutI }
  | { key: "TableLayout"; payload: TableLayoutI }
  | { key: "CalendarConfig"; payload: any }
);

export type ExistingLayout = { id: string } & (
  | CardsLayoutI
  | TimelineLayoutI
  | BoardLayoutI
  | TableLayoutI
);

export type UpdateTableItem = {
  id: string;
  dontCallAPI?: boolean;
  onSuccess: (() => void) | undefined;
} & (
  | { key: "CardsLayouts"; payload: CardsLayoutI }
  | { key: "TimelineLayouts"; payload: TimelineLayoutI }
  | { key: "BoardLayouts"; payload: BoardLayoutI }
  | { key: "TableLayouts"; payload: TableLayoutI }
  | { key: "CalendarConfig"; payload: any }
);

interface BaseLayout {
  Name: string;
  Fields: string[];
  ModuleAPIName?: string; // For Globals
  Type?: string; // For Globals
  GlobalID?: string;
}

export interface CardsLayoutI extends BaseLayout {
  BackFields: string[];
  CardType?: CardTypes;
  CardImage?: string;
}

interface TimelineLayoutI extends BaseLayout {
  GroupBy: string;
  TimeFrame: string;
}

interface BoardLayoutI extends BaseLayout {
  GroupBy: string;
  EmptyGroup?: boolean;
}

interface TableLayoutI extends BaseLayout {
  GroupBy?: string;
}

const usePageViewTable = ({
  currentViewID,
  updateView,
  parentViewID,
}: {
  currentViewID: string;
  updateView: any;
  parentViewID?: string;
}) => {
  const queryClient = useQueryClient();
  const viewIdRef = useRef<any>();
  const clientRef = useRef<QueryClient>(queryClient);
  viewIdRef.current = currentViewID;
  clientRef.current = queryClient;

  const createItem = useCallback<any>(
    ({ key, payload, onSuccess }: CreateTableItem) => {
      const viewID = viewIdRef.current;
      const finalPayload = {
        [key]: {
          ...payload,
          id: StrUtils.uuidv4(),
        },
      };

      updateView({ viewID, payload: finalPayload, parentViewID, onSuccess });
    },
    []
  );

  const updateItem = useCallback(
    ({ id, key, payload, dontCallAPI, onSuccess }: UpdateTableItem) => {
      const currentView = clientRef.current.getQueryData<View>([
        "view",
        viewIdRef.current,
      ]);

      if (!currentView) return;

      const items = currentView?.[key];

      if (!Array.isArray(items)) return;

      const newItems = items.map((item: any) => {
        if (item.id === id) {
          return {
            ...payload,
            id: id,
          };
        }
        return item;
      });

      clientRef.current.setQueryData(["view", viewIdRef.current], (prev: any) =>
        prev ? { ...prev, [key]: newItems } : prev
      );

      if (dontCallAPI) return newItems;

      updateView({
        viewID: viewIdRef.current,
        payload: { [key]: newItems, item: payload },
        onSuccess: onSuccess,
        parentViewID,
      });
    },
    []
  );

  const deleteItem = useCallback(
    ({ key, id }: { key: keyof View; id: string }) => {
      const currentView = clientRef.current.getQueryData<View>([
        "view",
        viewIdRef.current,
      ]);

      const newItems = currentView?.[key].filter((item: any) => item.id !== id);

      clientRef.current.setQueryData(["view", viewIdRef.current], (prev: any) =>
        prev ? { ...prev, [key]: newItems } : prev
      );

      updateView({
        viewID: viewIdRef.current,
        payload: { [key]: newItems },
        parentViewID,
      });
    },
    []
  );

  const reorderItems = useCallback(
    ({ key, newOrder }: { key: keyof View; newOrder: any }) => {
      const currentView = clientRef.current.getQueryData<View>([
        "view",
        viewIdRef.current,
      ]);
      const items = currentView?.[key];

      const newItems: any[] = [];

      for (const id of newOrder) {
        const item = _.findWhere(items, { id });
        if (item) {
          newItems.push(item);
        } else {
          // Prevent a crash if the item is missing
          return console.error(`Could not reorder ${key}`);
        }
      }

      clientRef.current.setQueryData(["view", viewIdRef.current], (prev) =>
        prev ? { ...prev, [key]: newItems } : prev
      );
      updateView({
        viewID: viewIdRef.current,
        payload: { [key]: newItems },
        parentViewID,
      });
    },
    []
  );

  return {
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
  };
};

export default usePageViewTable;
